import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";
import type { SuperAdminOrganizationDirectoryItem } from "@multi-chat/shared";
import { listSuperAdminOrganizationDirectory } from "../../api/super-admin-api.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import {
  AlertDialog,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import { SimpleTooltip, TooltipProvider } from "../../components/ui/tooltip/index.js";
import { useOrganizations } from "./organization-context.js";
import { OrganizationIcon } from "./OrganizationIcon.js";

const PAGE_SIZE = 25;

type DirectoryState =
  | { status: "loading"; items: SuperAdminOrganizationDirectoryItem[] }
  | { status: "ready"; items: SuperAdminOrganizationDirectoryItem[]; nextCursor: string | null }
  | { status: "error"; items: SuperAdminOrganizationDirectoryItem[] };

/**
 * Server-searched, server-paginated list of every organization a Platform
 * Admin may enter. Entering, adding, and removing only change client-side
 * selection and switcher listing; every request is authorized by the server.
 */
export function PlatformAdminOrganizationDirectoryDialog({
  open,
  onOpenChange,
  selectedOrganizationId,
  onEnter,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrganizationId: number | null;
  onEnter: (organizationId: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isInSwitcher, addToSwitcher, removeFromSwitcher } = useOrganizations();
  const [announcement, setAnnouncement] = useState("");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<DirectoryState>({ status: "loading", items: [] });

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setState((current) => ({ status: "loading", items: cursor ? current.items : [] }));
    listSuperAdminOrganizationDirectory(
      { search: submittedSearch || undefined, cursor, limit: PAGE_SIZE },
      controller.signal,
    ).then(
      (page) => {
        if (controller.signal.aborted) return;
        setState((current) => ({
          status: "ready",
          items: cursor ? [...current.items, ...page.items] : page.items,
          nextCursor: page.nextCursor,
        }));
      },
      () => {
        if (controller.signal.aborted) return;
        setState((current) => ({ status: "error", items: current.items }));
      },
    );
    return () => controller.abort();
  }, [open, submittedSearch, cursor, attempt]);

  function close(): void {
    onOpenChange(false);
  }

  // The organization chooser renders outside the app shell's tooltip provider.
  return (
    <TooltipProvider delayDuration={300}>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent
          className="w-[min(calc(100vw-2rem),32rem)]"
          data-testid="platform-admin-directory"
        >
          <AlertDialogCloseButton label={t("common.close")} onClose={close} />
          <AlertDialogTitle>{t("organization.platformAdmin.directoryTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("organization.platformAdmin.directoryDescription")}
          </AlertDialogDescription>
          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setCursor(undefined);
              setSubmittedSearch(search.trim());
            }}
          >
            <label className="sr-only" htmlFor="platform-admin-directory-search">
              {t("organization.platformAdmin.searchLabel")}
            </label>
            <input
              id="platform-admin-directory-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("organization.platformAdmin.searchPlaceholder")}
              className="h-9 min-w-0 flex-1 rounded-md border border-line bg-surface px-3 text-sm text-foreground"
              data-testid="platform-admin-directory-search"
            />
            <Button type="submit" size="sm" variant="outline">
              {t("organization.platformAdmin.search")}
            </Button>
          </form>

          <div className="mt-3 grid max-h-[min(22rem,50dvh)] gap-1 overflow-y-auto overscroll-contain">
            {state.items.map((organization) => {
              const current = organization.id === selectedOrganizationId;
              const listed = isInSwitcher(organization.id);
              const toggleLabel = listed
                ? t("organization.platformAdmin.removeFromSwitcher")
                : t("organization.platformAdmin.addToSwitcher");
              return (
                <div
                  key={organization.id}
                  className="flex min-w-0 items-center gap-1 rounded-md transition-colors hover:bg-surface-muted"
                  data-testid={`platform-admin-directory-row-${organization.id}`}
                  data-in-switcher={listed ? "true" : "false"}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md border-0 bg-transparent px-2 py-2 text-start focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]"
                    data-testid={`platform-admin-directory-option-${organization.id}`}
                    aria-current={current ? "true" : undefined}
                    onClick={() => onEnter(organization.id)}
                  >
                    <OrganizationIcon size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.8125rem] font-medium text-foreground">
                        <bdi>{organization.name}</bdi>
                      </span>
                      {listed || organization.status === "SUSPENDED" ? (
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          {listed ? (
                            <Badge
                              className="px-1.5 py-0 text-[0.6875rem] font-medium"
                              data-testid={`platform-admin-directory-in-switcher-${organization.id}`}
                            >
                              {t("organization.platformAdmin.inSwitcher")}
                            </Badge>
                          ) : null}
                          {organization.status === "SUSPENDED" ? (
                            <span className="text-[0.6875rem] font-medium text-warning">
                              {t("organization.suspended.badge")}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </span>
                  </button>
                  <SimpleTooltip content={toggleLabel}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="me-1 size-8 shrink-0 p-0 text-muted not-disabled:hover:text-foreground"
                      aria-label={
                        listed
                          ? t("organization.platformAdmin.removeFromSwitcherNamed", { name: organization.name })
                          : t("organization.platformAdmin.addToSwitcherNamed", { name: organization.name })
                      }
                      data-testid={`platform-admin-directory-toggle-${organization.id}`}
                      onClick={() => {
                        if (listed) {
                          removeFromSwitcher(organization.id);
                          setAnnouncement(
                            t("organization.platformAdmin.removedFromSwitcher", { name: organization.name }),
                          );
                        } else {
                          addToSwitcher(organization.id);
                          setAnnouncement(
                            t("organization.platformAdmin.addedToSwitcher", { name: organization.name }),
                          );
                        }
                      }}
                    >
                      {listed ? <Minus size={15} aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
                    </Button>
                  </SimpleTooltip>
                </div>
              );
            })}

            {state.status === "loading" ? (
              <div
                className="h-10 animate-pulse rounded-md bg-surface-muted"
                aria-busy="true"
                data-testid="platform-admin-directory-loading"
              />
            ) : null}

            {state.status === "ready" && state.items.length === 0 ? (
              <p className="m-0 px-2 py-3 text-sm text-muted" data-testid="platform-admin-directory-empty">
                {t("organization.platformAdmin.empty")}
              </p>
            ) : null}

            {state.status === "error" ? (
              <ProductErrorState
                testId="platform-admin-directory-error"
                layout="inline"
                title={t("organization.genericError.title")}
                description={t("organization.platformAdmin.loadError")}
                onRetry={() => setAttempt((value) => value + 1)}
                retryLabel={t("organization.platformAdmin.retry")}
              />
            ) : null}
          </div>

          {state.status === "ready" && state.nextCursor ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2 justify-self-start"
              data-testid="platform-admin-directory-more"
              onClick={() => setCursor(state.nextCursor ?? undefined)}
            >
              {t("organization.platformAdmin.loadMore")}
            </Button>
          ) : null}
          <p className="sr-only" aria-live="polite" data-testid="platform-admin-directory-announcement">
            {announcement}
          </p>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
